import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import * as monitorActions from '../../store/aiConversationMonitor/actions';
import * as acquisitionOrdersActions from '../../store/acquisitionOrders/actions';
import * as aiAgentsActions from '../../store/aiAgents/actions';
import { actualCountry } from '../../selectors/country';
import { selectAiAgents } from '../../selectors/aiAgent';
import {
  TAKE_BACK_ROLES,
  STOP_ALL_ROLES,
} from '../../config/aiConversationStatus';
import { timeLeft } from '../../config/aiConversationWindow';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import Modal from '../../components/Common/Modal/Modal';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import { ToggleColumnButton } from '../../components/Common/DataTable/tableActionButtons';
import AiConversations from '../../components/Leads/AiConversations';
import MonitorSummary from '../../components/AiConversations/MonitorSummary';
import BatchAiConversationBar from '../../components/AiConversations/BatchAiConversationBar';
import StopAllButton from '../../components/AiConversations/StopAllButton';
import createTableColumns from '../../components/AiConversations/tableColumns';
import { addOrRemove } from '../../helpers/sharedFunction';

// Often enough for a countdown in minutes, rarely enough to cost nothing.
const TICK_MS = 30 * 1000;

// The day on the country's clock: the floor it shows runs on it, wherever
// the browser is. Without a time zone, the browser's.
const dayOf = (date, timezone) =>
  (timezone ? moment(date).tz(timezone) : moment(date)).format('YYYY-MM-DD');

/**
 * The conversations the agent is holding right now, for whoever runs the
 * floor: how many, how long each still has, and the way to take them back.
 */
const Conversations = () => {
  const loading = useSelector((state) => state.AiConversationMonitor.loading);
  const live = useSelector((state) => state.AiConversationMonitor.live);
  const today = useSelector((state) => state.AiConversationMonitor.today);
  const leads = useSelector((state) => state.AiConversationMonitor.leads);
  const liveError = useSelector(
    (state) => state.AiConversationMonitor.liveError,
  );
  const todayError = useSelector(
    (state) => state.AiConversationMonitor.todayError,
  );
  const acquisitionOrders = useSelector(
    (state) => state.AcquisitionOrder.acquisitionOrders,
  );
  const aiAgents = useSelector(selectAiAgents());
  const country = useSelector(actualCountry);
  const role = useSelector((state) => state.Auth.admin.role);
  const countryId = useSelector((state) => state.Dashboard.countryId);
  const timezone = country?.timezone;
  const showSensitiveData = useSelector(
    (state) => state.Layout.showSensitiveData,
  );

  const canTakeBack = TAKE_BACK_ROLES.includes(role);
  const canStopAll = STOP_ALL_ROLES.includes(role);

  const [now, setNow] = useState(new Date());
  const [selectedIds, setSelectedIds] = useState([]);
  const [openLeadId, setOpenLeadId] = useState(null);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([
    { columnName: 'timeLeft', direction: 'asc' },
  ]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Per country: the listeners read the country id once, so a switch while
  // this page is open resets the store and starts them again on the new one.
  // The time zone too, since "today" starts at the country's midnight: it
  // arrives with the countries, possibly after the first mount.
  useEffect(() => {
    dispatch(monitorActions.syncLiveAiConversations());
    dispatch(monitorActions.syncTodayAiConversations());
    dispatch(acquisitionOrdersActions.syncAcquisitionOrders());
    // The agents only feed the brake's choice of agent.
    if (canStopAll) dispatch(aiAgentsActions.syncAiAgents());
    // Only the monitor's own store: the acquisition orders and the agents are
    // shared with other pages.
    return () => dispatch(monitorActions.resetState());
  }, [countryId, timezone]);

  // The countdown, and the day the "today" listener reads from: past
  // midnight it is asked again, and takeLatest drops the old one.
  const day = useRef(null);
  useEffect(() => {
    day.current = dayOf(new Date(), timezone);
    const timer = setInterval(() => {
      const next = new Date();
      const nextDay = dayOf(next, timezone);
      if (nextDay !== day.current) {
        day.current = nextDay;
        dispatch(monitorActions.syncTodayAiConversations());
      }
      setNow(next);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [timezone]);

  // A conversation taken back leaves the list: it must leave the selection
  // too, or the bar would go on counting it.
  useEffect(() => {
    setSelectedIds((ids) => ids.filter((id) => live.some((c) => c.id === id)));
  }, [live]);

  const openConversation = useCallback((row) => setOpenLeadId(row.leadId), []);

  const columns = useMemo(
    () =>
      createTableColumns({ openConversation, showSensitiveData, t, timezone }),
    [openConversation, showSensitiveData, t, timezone],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) =>
    setHiddenColumns(addOrRemove(hiddenColumns, name));

  const rows = useMemo(
    () =>
      live.map((conversation) => {
        const order = acquisitionOrders.find(
          ({ id }) => id === conversation.acquisitionOrderId,
        );
        return {
          ...conversation,
          leadName: (leads[conversation.leadId] || {}).name || '',
          acquisitionOrderName:
            (order && order.name) || conversation.acquisitionOrderId || '',
          timeLeft: timeLeft(conversation, now),
        };
      }),
    [live, leads, acquisitionOrders, now],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={t('AI Conversations')} />
          <MonitorSummary
            live={live}
            today={today}
            country={country}
            now={now}
            loading={loading}
            liveError={liveError}
            todayError={todayError}
            // The administrators are the ones who can create a missing index
            // or fix a rule; the floor only needs to know the numbers are off.
            showDetail={canStopAll}
          />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    {canStopAll && (
                      <StopAllButton
                        agents={aiAgents.map(({ id, name }) => ({ id, name }))}
                      />
                    )}
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    selection={canTakeBack ? selectedIds : undefined}
                    onSelectionChange={canTakeBack ? setSelectedIds : undefined}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {canTakeBack && (
        <BatchAiConversationBar
          selectedIds={selectedIds}
          onDone={() => setSelectedIds([])}
        />
      )}
      <Modal
        size="xl"
        title="AI conversations"
        isOpen={!!openLeadId}
        toggle={() => setOpenLeadId(null)}
        showClose
        scrollable={false}
      >
        {/* Mounted only while open: it reads the lead's conversations on
            mount, and only uses the id. */}
        {openLeadId && <AiConversations lead={{ id: openLeadId }} />}
      </Modal>
    </React.Fragment>
  );
};

export default Conversations;
